import { Product } from '@gripp/shared-logic';
import { Button, Colors } from '@gripp/shared-ui';
import { formStyles } from 'libs/shared-ui/src/styles/forms';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Modal, StyleSheet, Text, View } from 'react-native';
import { ProductAssignmentForm, ProductAssignmentType } from './productAssignmentForm';

interface ProductAddOnsProps {
  product: Product;
}

// Main ProductAddOns component
export const ProductAddOns: React.FC<ProductAddOnsProps> = ({
  product
}) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const toggleModal = (isVisible: boolean) => {
    setIsModalVisible(isVisible);
  };

  const renderAddonItem = (addon: { id: string; name: string }, index: number) => {
    return (
      <View 
        style={styles.addonContainer} 
        key={`render-addon-item-${index}`}
      >
        <View style={styles.addonItem}>
          <Text style={styles.addonText}>{addon.name}</Text>
        </View>
      </View>
    )
  };

  return (
    <>
      <View style={styles.card}>
        <Text style={formStyles.formItemTitle}>
          {t('product.detail.addon_options')}
        </Text>
        <View style={styles.addonsContainer}>
          {(product.addons?.length ?? 0) > 0 ? (
            product.addons?.filter((addon) => addon != null).map((addon, idx) => {
              return renderAddonItem(addon, idx)
            })
          ) : (
            <Text style={styles.noAddonsText}>
              {t('product.detail.no_addon_options')}
            </Text>
          )}

          <Button
            state='default'
            onPress={() => toggleModal(true)}
            variant='rectangular'
            size='large'
          >
            {`+ ${t('product.detail.add_more')}`}
          </Button>
        </View>
      </View>

      {/* Modal for ProductAssignmentForm */}
      <Modal
        visible={isModalVisible}
        animationType="fade"
        presentationStyle="pageSheet"
        transparent
        onRequestClose={() => toggleModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.container}>
            <ProductAssignmentForm
              productId={product.id}
              title={product.name}
              assignmentType={ProductAssignmentType.Addons}
              showTabs={false}
              onComplete={() => toggleModal(false)}
            />
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  // Main card styles
  card: {
    backgroundColor: Colors.role.surface,
    padding: 20,
    borderRadius: 8,
    gap: 20,
  },
  cardHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.role.textStrong,
  },
  addonsContainer: {
    flexDirection: 'column',
    gap: 20,
  },
  noAddonsText: {
    fontSize: 16,
    color: Colors.role.textSecondary,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  addButtonText: {
    fontSize: 14,
    color: Colors.primaryDark,
    fontWeight: '500',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 7,
    backgroundColor: Colors.role.backdrop,
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 560,
    maxHeight: Dimensions.get('window').height - 250,
    backgroundColor: Colors.role.surfaceRaised,
    borderRadius: 12,
  },
  addonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  addonItem: {
    backgroundColor: Colors.role.surfaceHover,
    borderRadius: 8,
    height: 44,
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  addonText: {
    fontSize: 16,
    color: Colors.role.textStrong,
  },
});